import { createSelector, createSlice, PayloadAction, WritableDraft } from "@reduxjs/toolkit";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import TeamTemplateQuerySchema, { TeamTemplateQueryJSON } from "work/team-templates/_api/queries/_schema/team-template-query-schema";
import TeamTemplateQuery from "work/team-templates/_api/queries/team-template-query";
import { teamTemplatesApi } from "work/team-templates/_api/team-template-api";
import { TeamTemplateJSON } from "work/team-templates/_schema/team-template-schema";
import TeamTemplate from "work/team-templates/team-template";

export type TeamTemplatesStoreState = {
  byId: {
    entries: Record<string, TeamTemplateJSON | null | undefined>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, Paginated<TeamTemplateJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, TeamTemplateQueryJSON>;
  };
};

const initialState: TeamTemplatesStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

const resetByQueryReducer = (state: WritableDraft<TeamTemplatesStoreState>) => {
  state.byQuery = initialState.byQuery;
};

function cacheTeamTemplate(state: WritableDraft<TeamTemplatesStoreState>, teamTemplateJSON: TeamTemplateJSON) {
  const id = teamTemplateJSON.id;
  if (!id) return;

  state.byId.entries[id] = teamTemplateJSON;
  state.byId.loading[id] = false;
  state.byId.error[id] = null;
}

function removeCachedTeamTemplate(state: WritableDraft<TeamTemplatesStoreState>, id: Guid) {
  const idValue = id.value;
  state.byId.entries[idValue] = null;
  state.byId.loading[idValue] = false;
  state.byId.error[idValue] = null;
  resetByQueryReducer(state);
}

function getRejectedMessage(payload: unknown) {
  return getErrorMessageFromUnknown(payload) ?? "An error occurred.";
}

const teamTemplatesSlice = createSlice({
  name: "team-templates",
  initialState,
  reducers: {
    updateTeamTemplatesInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: TeamTemplateQuery;
      }>,
    ) => {
      const { interfaceName, query } = action.payload;
      state.interfaceQueries.entries[interfaceName] = TeamTemplateQuerySchema.serialize(query);
    },
    upsertTeamTemplate: (state, action: PayloadAction<TeamTemplateJSON>) => {
      cacheTeamTemplate(state, action.payload);
      resetByQueryReducer(state);
    },
    removeTeamTemplate: (state, action: PayloadAction<Guid>) => {
      removeCachedTeamTemplate(state, action.payload);
    },
    resetByQuery: resetByQueryReducer,
  },
  extraReducers: (builder) => {
    builder.addMatcher(teamTemplatesApi.endpoints.getTeamTemplates.matchPending, (state, action) => {
      const query = action.meta.arg.originalArgs.query;
      if (!query?.filterParameters) return;

      const queryKey = query.cacheKey;
      state.byQuery.loading[queryKey] = true;
      state.byQuery.error[queryKey] = null;
    });

    builder.addMatcher(teamTemplatesApi.endpoints.getTeamTemplates.matchFulfilled, (state, action) => {
      const query = action.meta.arg.originalArgs.query;
      if (!query?.filterParameters) return;

      const queryKey = query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.entries[queryKey] = PaginatedResponse.from(action.payload.data, action.payload.headers);
      state.byQuery.error[queryKey] = null;

      for (const templateJson of action.payload.data) {
        cacheTeamTemplate(state, templateJson);
      }
    });

    builder.addMatcher(teamTemplatesApi.endpoints.getTeamTemplates.matchRejected, (state, action) => {
      const query = action.meta.arg.originalArgs.query;
      if (!query?.filterParameters) return;

      const queryKey = query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.error[queryKey] = getRejectedMessage(action.payload);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.getTeamTemplateById.matchPending, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });

    builder.addMatcher(teamTemplatesApi.endpoints.getTeamTemplateById.matchFulfilled, (state, action) => {
      if (action.payload === null) {
        const id = action.meta.arg.originalArgs.templateId;
        state.byId.entries[id] = null;
        state.byId.loading[id] = false;
        state.byId.error[id] = null;
        return;
      }

      const id = action.payload.id ?? action.meta.arg.originalArgs.templateId;
      cacheTeamTemplate(state, { ...action.payload, id });
    });

    builder.addMatcher(teamTemplatesApi.endpoints.getTeamTemplateById.matchRejected, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = false;
      state.byId.error[id] = getRejectedMessage(action.payload);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.createTeamTemplate.matchFulfilled, (state, action) => {
      cacheTeamTemplate(state, action.payload);
      resetByQueryReducer(state);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.updateTeamTemplate.matchPending, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });

    builder.addMatcher(teamTemplatesApi.endpoints.updateTeamTemplate.matchFulfilled, (state, action) => {
      const id = action.payload.id ?? action.meta.arg.originalArgs.templateId;
      cacheTeamTemplate(state, { ...action.payload, id });
      resetByQueryReducer(state);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.updateTeamTemplate.matchRejected, (state, action) => {
      const { originalTemplate, templateId } = action.meta.arg.originalArgs;
      state.byId.entries[templateId] = originalTemplate.toJSON();
      state.byId.loading[templateId] = false;
      state.byId.error[templateId] = getRejectedMessage(action.payload);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.deleteTeamTemplate.matchPending, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });

    builder.addMatcher(teamTemplatesApi.endpoints.deleteTeamTemplate.matchFulfilled, (state, action) => {
      removeCachedTeamTemplate(state, new Guid(action.meta.arg.originalArgs.templateId));
    });

    builder.addMatcher(teamTemplatesApi.endpoints.deleteTeamTemplate.matchRejected, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = false;
      state.byId.error[id] = getRejectedMessage(action.payload);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.archiveTeamTemplate.matchPending, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });

    builder.addMatcher(teamTemplatesApi.endpoints.archiveTeamTemplate.matchFulfilled, (state, action) => {
      const id = action.payload.id ?? action.meta.arg.originalArgs.templateId;
      cacheTeamTemplate(state, { ...action.payload, id });
      resetByQueryReducer(state);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.archiveTeamTemplate.matchRejected, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = false;
      state.byId.error[id] = getRejectedMessage(action.payload);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.unarchiveTeamTemplate.matchPending, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });

    builder.addMatcher(teamTemplatesApi.endpoints.unarchiveTeamTemplate.matchFulfilled, (state, action) => {
      const id = action.payload.id ?? action.meta.arg.originalArgs.templateId;
      cacheTeamTemplate(state, { ...action.payload, id });
      resetByQueryReducer(state);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.unarchiveTeamTemplate.matchRejected, (state, action) => {
      const id = action.meta.arg.originalArgs.templateId;
      state.byId.loading[id] = false;
      state.byId.error[id] = getRejectedMessage(action.payload);
    });

    builder.addMatcher(teamTemplatesApi.endpoints.registerTeamTemplateToMarketplace.matchFulfilled, (state, action) => {
      const id = action.payload.id ?? action.meta.arg.originalArgs.templateId;
      cacheTeamTemplate(state, { ...action.payload, id });
      resetByQueryReducer(state);
    });
  },
});

const selectTeamTemplateById =
  createSelector(
    [
      (state: RootState) => state.teamTemplates.byId.entries,
      (_: RootState, id?: Guid) => id?.value,
    ],
    (entries, id) => {
      if (!id) return;
      const json = entries[id];
      if (!json) return;
      return TeamTemplate.fromJSON(json);
    },
  );
export const getTeamTemplateById = (id?: Guid) => (state: RootState) =>
  selectTeamTemplateById(state, id);
export const getIsLoadingTeamTemplateById = (id?: Guid) => (state: RootState) =>
  state.teamTemplates.byId.loading[id?.value ?? ""] ?? false;
export const getErrorLoadingTeamTemplateById = (id?: Guid) => (state: RootState) =>
  state.teamTemplates.byId.error[id?.value ?? ""] ?? false;

const selectTeamTemplatesByQuery =
  createSelector(
    [
      (state: RootState) => state.teamTemplates.byQuery.entries,
      (_: RootState, query?: TeamTemplateQuery | null) => query?.cacheKey,
    ],
    (entries, queryKey) => {
      if (!queryKey) return;
      const pagedJson = entries[queryKey];
      if (!pagedJson) return;
      const teamTemplates = TeamTemplate.fromJSONArray(pagedJson.data);
      return PaginatedResponse.from<TeamTemplate>(teamTemplates, pagedJson.headers);
    },
  );
export const getTeamTemplatesByQuery = (query?: TeamTemplateQuery | null) => (state: RootState) =>
  selectTeamTemplatesByQuery(state, query);
export const getIsLoadingTeamTemplatesByQuery = (query?: TeamTemplateQuery | null) => (state: RootState) => {
  if (!query) return false;
  return state.teamTemplates.byQuery.loading[query?.cacheKey ?? ""] ?? false;
};
export const getErrorLoadingTeamTemplatesByQuery = (query?: TeamTemplateQuery | null) => (state: RootState) => {
  if (!query) return false;
  return state.teamTemplates.byQuery.error[query?.cacheKey ?? ""] ?? false;
};

const selectTeamTemplatesQueryByInterface =
  createSelector(
    [
      (state: RootState) => state.teamTemplates.interfaceQueries.entries,
      (_: RootState, interfaceName: string) => interfaceName,
    ],
    (entries, interfaceName) => {
      const json = entries[interfaceName];
      if (!json) return;
      return TeamTemplateQuery.fromJSON(json);
    },
  );
export const getTeamTemplatesQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectTeamTemplatesQueryByInterface(state, interfaceName);

export const { removeTeamTemplate, updateTeamTemplatesInterfaceQuery, upsertTeamTemplate } = teamTemplatesSlice.actions;

export default teamTemplatesSlice;
